import logProPurchaseGetStartedClicked from '@alltrails/analytics/events/logProPurchaseGetStartedClicked';
import logProPurchasePageViewed from '@alltrails/analytics/events/logProPurchasePageViewed';
import NoSsr from '@material-ui/core/NoSsr';
import Typography from '@alltrails/shared/denali/components/Typography';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import { lazy, Suspense, useEffect, ReactNode } from 'react';
import { defineMessage, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import Texture from '@alltrails/shared/denali/components/Texture';
import Coupon from 'types/Coupon';
import Plan from 'types/Plan';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { wrapUrlSafe } from 'utils/language_support_util';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import chevronDownBouncing from 'components/shared/ChevronDownBouncing/chevron-down-bouncing.svg';
import useFeatures from 'hooks/useFeatures';
import useExperiment from 'hooks/useExperiment';
import useToggle from '@alltrails/shared/hooks/useToggle';
import CheckoutModal from 'components/CheckoutModal';
import SignupModal from 'components/SignupModal';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import { UserId } from '@alltrails/shared/types/User';
import * as styles from './PlusLandingPageBase.module.scss';
import heroDesktopJpg from './images/hero-2800w.jpg';
import heroDesktopWebp from './images/hero-2800w.webp';
import heroDesktopAvif from './images/hero-2800w.avif';
import heroMobileJpg from './images/hero-768w.jpg';
import heroMobileWebp from './images/hero-768w.webp';
import heroMobileAvif from './images/hero-768w.avif';

const ChevronDownBouncing = lazy(() => import('components/shared/ChevronDownBouncing'));

function PrimaryLink({ buttonText, coupon, plan, to, userId }: { buttonText: ReactNode; plan: Plan; coupon: Coupon; to: string; userId: UserId }) {
  const languageRegionCode = useLanguageRegionCode();
  const experiment = useExperiment('growth-on-page-web-checkout');
  const [isCheckoutModalOpen, toggleCheckoutModal] = useToggle();
  const [isSignupModalOpen, toggleSignupModal] = useToggle();

  return (
    <div className={styles.buttonContainer}>
      <CheckoutModal coupon={coupon} isOpen={isCheckoutModalOpen} onRequestClose={toggleCheckoutModal} />
      <SignupModal
        heading={<FormattedMessage defaultMessage="Do more outdoors{br}with AllTrails+" values={{ br: <br /> }} />}
        trigger={CarouselDisplayTrigger.DirectNav}
        isOpen={isSignupModalOpen}
        onRequestClose={() => {
          toggleSignupModal();
        }}
        onSuccess={() => {
          toggleSignupModal();
          toggleCheckoutModal();
        }}
      />
      {/*
        Two buttons are needed in order to show different button variants based on screen width.
        Setting the variant prop to the 'useIsMobile' value results in flickering on page load.
      */}
      <Button
        className={styles.desktopButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        {...(experiment?.value !== 'treatment' ? { linkInfo: { href: wrapUrlSafe(to, languageRegionCode), useReactRouter: true } } : {})}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });

          if (experiment?.value === 'treatment') {
            if (!userId) {
              toggleSignupModal();
            } else {
              toggleCheckoutModal();
            }
          }
        }}
        testId="simple-pro-landing_page-primary"
        variant="accent"
      />
      <Button
        className={styles.mobileButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        {...(experiment?.value !== 'treatment' ? { linkInfo: { href: wrapUrlSafe(to, languageRegionCode), useReactRouter: true } } : {})}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });

          if (experiment?.value === 'treatment') {
            if (!userId) {
              toggleSignupModal();
            } else {
              toggleCheckoutModal();
            }
          }
        }}
        testId="simple-pro-landing_page-primary"
        variant="primary"
        allowWrap
      />
    </div>
  );
}

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1591%3A72835

type Props = {
  buttonText: ReactNode;
  coupon?: Coupon;
  headline: ReactNode;
  subhead: ReactNode;
  title: ReactNode;
  userId?: UserId;
  plan: Plan;
};

export default function PlusLandingPageBase({ buttonText, coupon, headline, subhead, title, userId, plan }: Props) {
  useEffect(() => {
    logProPurchasePageViewed({
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
    });
  }, [plan, coupon]);

  const intl = useIntl();
  const heroAltText = defineMessage({
    defaultMessage: 'A group of people hiking down a hill'
  });

  const user = useUser();
  const flipperFeatures = useFeatures();
  const areGarminCoursesEnabled = flipperFeatures.includes('garmin_courses');

  const rttcv2 = useExperiment('web-trails-trail-conditions-v2')?.value === 'rttc-v2';

  const features = [
    { pro: false, text: <FormattedMessage defaultMessage="Navigate on the trail" /> },
    { pro: false, text: <FormattedMessage defaultMessage="Favorite trails and create lists" /> },
    ...(areGarminCoursesEnabled ? [{ new: true, pro: true, text: <FormattedMessage defaultMessage="Send routes to Garmin" /> }] : []),
    { pro: true, text: <FormattedMessage defaultMessage="Download offline maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Get alerts for wrong turns" /> },
    ...(rttcv2 ? [{ new: true, pro: true, text: <FormattedMessage defaultMessage="Know conditions before you go" /> }] : []),
    { new: true, pro: true, text: <FormattedMessage defaultMessage="Preview 3D Flyovers" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Find trails by distance from you" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Unlock more map details" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Print maps for backup" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Bring trails to life with 3D maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Explore ad free" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Give back with 1% for the Planet" /> }
  ];

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const search = params.toString();

  const to = user || userId ? `/plus/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileAvif} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileWebp} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileJpg} type="image/jpg" />
          <source srcSet={heroDesktopAvif} type="image/avif" />
          <source srcSet={heroDesktopWebp} type="image/webp" />
          <img alt={intl.formatMessage(heroAltText)} src={heroDesktopJpg} />
        </picture>
        <Texture variant="plus" className={styles.texture} />
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.headline}>{headline}</div>
          <div className={styles.title}>{title}</div>
          <div className={styles.subhead}>{subhead}</div>
          <div className={styles.button}>
            <PrimaryLink buttonText={buttonText} plan={plan} coupon={coupon} to={to} userId={userId || user?.id} />
            <Typography className={styles.cancel} component="div" mb="8" variant="text100">
              <FormattedMessage defaultMessage="Cancel anytime." />
            </Typography>
          </div>
          <Typography className={styles.lottie} component="div" mb="8">
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </Typography>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>
    </div>
  );
}
